import { useRouteToFromConfigurator } from "@lib/hooks/useRouteToFromConfigurator";
import { getAttributeDataById } from "@lib/utils/getAttributeDataById";
import { CLAIMS_DOMAIN, useAuthentication } from "@providers/AuthenticationProvider";
import { getCorePresetFields, getSizeSelection } from "@providers/BasketProvider/helpers";
import { FixedConfigurationStep } from "@providers/ConfiguratorProviderV2/helpers/stepperHelpers";
import { useCreateConfigItem } from "@providers/ConfiguratorProviderV2/hooks/useCreateConfigItem";
import { ErrorStatus, useErrorContext } from "@providers/ErrorProvider";
import {
  AttributeSelection,
  AttributeValueType,
  BasicValue,
  ConfigurationItem,
  EngineResponse,
  ProductAttribute,
  ProductMain,
  ProductType,
  SanityProductPreset,
  SizeValue,
  TransportPriority,
} from "@sourceful/shared-types";
import { getQuantitySelection } from "@sourceful/shared-utils/engine-utils";
import { useRouter } from "next/router";
import { FunctionComponent, createContext, useCallback, useContext, useMemo } from "react";
import { usePresetDetailState } from "./usePresetDetailState";

export type OnCustomiseClicked = (options: { preset: SanityProductPreset | null }) => void;
export type OnDesignNowClicked = (options: { preset: SanityProductPreset | null }) => void;
export type OnRequestQuoteClicked = (options: { preset: SanityProductPreset | null }) => void;

export type CreateConfigItemFromPreset = (options: {
  preset: SanityProductPreset | null;
  launchConfigurator?: boolean;
  initialConfiguratorStep?: FixedConfigurationStep;
  onLaunchingConfigurator?: () => void;
  onLaunchEnd?: () => void;
}) => Promise<ConfigurationItem | null>;

export interface ProductDetailProviderInjectedProps {
  selectedPreset: SanityProductPreset | null;
  createConfigItemFromPreset: CreateConfigItemFromPreset;
  quantityAttribute: ProductAttribute | null;
  engineMetadata: EngineResponse | null;
  updatePresetQuantity: (value: AttributeValueType) => void;
  updatePresetSize: (value: AttributeValueType, isCustomSize: boolean) => void;
  presetConfigurationEnabled: boolean;
  onRequestQuoteClicked: OnRequestQuoteClicked;
}

export interface ProductDetailProviderProps {
  product: ProductMain;
  presets: SanityProductPreset[];
  presetOverride?: SanityProductPreset;
  presetOverrideEngineMetadata?: EngineResponse;
  children?: React.ReactNode;
}

const ProductDetailContext = createContext({} as ProductDetailProviderInjectedProps);

const useProductDetailContext = () => useContext(ProductDetailContext);

const ProductDetailProvider: FunctionComponent<ProductDetailProviderProps> = ({
  children,
  product,
  presets,
  presetOverride,
  presetOverrideEngineMetadata,
}) => {
  const createConfigurationItem = useCreateConfigItem();
  const { user, organisation, isLoading, waitForUser } = useAuthentication();

  const router = useRouter();
  const { routeToFromConfigurator } = useRouteToFromConfigurator(router);

  const { pushError } = useErrorContext();

  const currentPath = `/buy/${product.slug}`;

  const quantityAttributeId = product.attributes.ids.find((attributeId: string) => {
    const attribute = getAttributeDataById(product, attributeId);
    return attribute?.type === "quantity";
  });

  const quantityAttribute = quantityAttributeId
    ? getAttributeDataById(product, quantityAttributeId)
    : null;

  const initialPreset = useMemo(() => {
    return presetOverride ?? presets.find(p => p.isDefault) ?? presets[0];
  }, [presetOverride, presets]);

  const { preset, engineMetadata, updatePresetQuantity, updatePresetSize } = usePresetDetailState({
    initialPreset,
    initialEngineResponse: presetOverrideEngineMetadata ?? null,
    productType: product.type,
  });

  const routeToQuote = useCallback(
    (preset: SanityProductPreset) => {
      const urlParams = new URLSearchParams();

      const quantityAttr = getQuantitySelection(preset.attributes);

      if (quantityAttr) {
        urlParams.append(quantityAttr.attribute.id, (quantityAttr.value as BasicValue).value);
      }

      const sizeAttr = getSizeSelection(preset.attributes);

      if (sizeAttr) {
        const size = sizeAttr.value as SizeValue;
        // check all options to see if it exists, if not its a custom size
        const isCustom = sizeAttr.attribute.options.every(option => {
          return option.value.id !== size.id;
        });

        if (isCustom) {
          sizeAttr.attribute?.requiredDimensions?.forEach(dimension => {
            urlParams.append(dimension, size[dimension]?.toString() ?? "");
          });
        } else {
          urlParams.append(sizeAttr.attribute.id, size.id);
        }
      }

      let url = `/buy/${
        presetOverride ? preset.slug : product.slug
      }/lead-gen?${urlParams.toString()}`;

      router.push(url);
    },
    [presetOverride, product.slug, router]
  );

  const routeToConfigurator = useCallback(
    (basketItemId: number | string | null) => {
      let url = `${currentPath}/customise?basketItemId=${basketItemId}`;

      routeToFromConfigurator(url);
    },
    [currentPath, routeToFromConfigurator]
  );

  const createConfigItemFromPreset: CreateConfigItemFromPreset = useCallback(
    async ({ preset, launchConfigurator = true, onLaunchingConfigurator, onLaunchEnd }) => {
      try {
        if (isLoading) await waitForUser;

        onLaunchingConfigurator && onLaunchingConfigurator();

        if (!quantityAttribute) {
          throw new Error("No quantity attribute found");
        }

        if (!preset) {
          throw new Error("No Preset selected!");
        }

        const basketPreset = getCorePresetFields(preset);

        const qtyAttribute = Object.values(preset.attributes).find(attributeSelection => {
          //@ts-ignore
          return attributeSelection.attribute.type === "quantity";
        });

        if (!qtyAttribute) {
          throw new Error(`Could not find qty attribute for preset ${preset.id}`);
        }

        const defaultQty = Number(((qtyAttribute as AttributeSelection).value as BasicValue).value);

        const defaultTransportPriority = preset.defaultTransportPriority || TransportPriority.PRICE;

        const userId = user?.sub || "";
        const userUuidInternal = user ? user[CLAIMS_DOMAIN].user.uuid : "";

        const newConfigurationItem = await createConfigurationItem({
          name: product.name,
          hasChanged: false,
          quantity: defaultQty,
          product,
          attributeSelection: {
            ...preset.attributes,
          },
          preset: basketPreset,
          artworkThumbnails: undefined,
          baseProductId: preset.baseProductId,
          baseProductVersionId: preset.baseProductVersionId,
          engineMetadata: null,
          artwork: {
            assets: {},
          },
          currency: "GBP",
          userId: userId,
          userUuidInternal: userUuidInternal,
          orgId: organisation?.id || "",
          metadata: {
            transportPriority: defaultTransportPriority,
          },
        });

        if (launchConfigurator) {
          routeToConfigurator(newConfigurationItem.id);
          onLaunchEnd && setTimeout(() => onLaunchEnd(), 1000);
        }

        return newConfigurationItem;
      } catch (error: any) {
        onLaunchEnd && onLaunchEnd();
        pushError(
          "ProductDetail",
          {
            status: ErrorStatus.fatal,
            message: error?.message || "Failed to launch configurator",
            stack: error,
          },
          true
        );
        return null;
      }
    },
    [
      createConfigurationItem,
      isLoading,
      product,
      pushError,
      quantityAttribute,
      organisation?.id,
      user?.sub,
      waitForUser,
    ]
  );

  const onRequestQuoteClicked: OnRequestQuoteClicked = useCallback(
    ({ preset }) => {
      if (!preset) return;

      // TODO: Tracking?

      routeToQuote(preset);
    },
    [routeToQuote]
  );

  const presetConfigurationEnabled = useMemo(() => {
    return product.type !== ProductType.RFQ;
  }, [product.type]);

  return (
    <ProductDetailContext.Provider
      value={{
        selectedPreset: preset,
        createConfigItemFromPreset,
        quantityAttribute: quantityAttribute ?? null,
        engineMetadata,
        updatePresetQuantity,
        presetConfigurationEnabled,
        updatePresetSize,
        onRequestQuoteClicked,
      }}
    >
      {children}
    </ProductDetailContext.Provider>
  );
};

export { ProductDetailContext, ProductDetailProvider, useProductDetailContext };
